import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react"
import { Platform } from "react-native"

import { filterOfferedSlotIds } from "../constants/summerContent"

const STORAGE_KEY = "summer_funnel_state_v2"

export const SUMMER_FUNNEL_INITIAL = {
    funnelStateId: null,
    // Pré-rempli depuis la LP (submissionGUID HubSpot ou query params).
    studentFirstname: "",
    parentEmail: "",
    submissionGUID: null,
    // Étape classe — année en cours + rentrée (dérivée, corrigeable).
    studentClass: null,
    studentClassRentree: null,
    // Étape diagnostic — matières (max 3) / niveau / objectif / créneaux.
    subjects: [],
    objective: null,
    niveauActuel: null,
    // Créneaux choisis au configurateur (5 jours × 1 h/2 h). Le pack d'heures =
    // somme des durées. Le récap + paiement 1 € est sur l'écran checkout.
    selectedSlotIds: [],
    // Répartition heures/matière choisie ({subjectId: heures}). Vide = split auto
    // (le backend retombe sur le split égal). Honorée si cohérente.
    subjectHours: {},
    // Mois de l'édition choisis (numéros de mois). AUCUNE pré-sélection : le
    // parent coche lui-même (juillet, août ou les deux) ; la reprise réhydrate
    // les mois déjà choisis depuis checkout_params.
    selectedMonths: [],
    // Variante d'engagement servie par le lien fast-track (admin). Défaut = SANS
    // engagement (false). Pilote la grille (commitment_12m) + le libellé checkout.
    withCommitment: false,
    // Chemin « sans 1 € » (lien fast-track admin) : souscrit directement sur le
    // moyen de paiement déjà au dossier, sans dépôt de pré-inscription. Défaut false
    // (= flux 1 € standard). N'a d'effet que si un PM est au dossier (sinon 422).
    skipDeposit: false,
    // Date ISO de la 1ʳᵉ séance réelle du lead (calcul backend : 1ʳᵉ semaine
    // complète + règle de délai min hors dimanche), renvoyée par l'endpoint
    // programme. Affichée au checkout ("Démarrage").
    firstSession: null,
    // Option de date de démarrage choisie ("YYYY-MM-DD", endpoint start-options).
    // Persistée backend (answers / create-checkout-session) — pilote la 1ʳᵉ
    // séance ET le prorata de la 1ʳᵉ facture.
    startDate: null,
    // Montant (centimes) de la 1ʳᵉ facture proratisée, prélevée le jour de la
    // 1ʳᵉ séance — servi par start-options pour l'option choisie. Affichage
    // checkout + confirmation ; le backend fige sa propre valeur à la conversion.
    firstChargeAmountCents: null,
    paid: false,
}

const loadFromStorage = () => {
    if (Platform.OS !== "web") return null
    try {
        const raw = window?.sessionStorage?.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch (_e) {
        return null
    }
}

const saveToStorage = (state) => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (_e) {
        /* noop */
    }
}

const clearStorage = () => {
    if (Platform.OS !== "web") return
    try {
        window?.sessionStorage?.removeItem(STORAGE_KEY)
    } catch (_e) {
        /* noop */
    }
}

/**
 * Retire les créneaux SATURÉS de l'état du funnel. Appliqué au bord du Context —
 * à l'hydratation ET à chaque `update` — parce que tous les écrans aval lisent
 * `state.selectedSlotIds` directement (reveal programme, checkout) : un état
 * ressuscité du sessionStorage d'AVANT la fermeture d'un créneau les enverrait
 * sinon poster un créneau que le back refuse, et le parent se retrouverait
 * bloqué au paiement sans self-heal. Un seul point de passage plutôt qu'un
 * filtre à recopier sur chaque écran.
 *
 * Le Context n'a pas la config funnel de l'API : il applique le repli hors-ligne
 * (`SUMMER_FULL_SLOT_IDS`). Le configurateur affine ensuite avec la config servie
 * — la seule divergence possible est un créneau ROUVERT côté backend mais encore
 * listé dans la constante : il serait retiré ici, et le parent le re-cocherait
 * dans la grille (qui, elle, suit l'API).
 */
const withOfferedSlotIds = (state) =>
    state.selectedSlotIds?.length
        ? {
              ...state,
              selectedSlotIds: filterOfferedSlotIds(state.selectedSlotIds),
          }
        : state

const SummerFunnelContext = createContext(null)

/**
 * Provider UNIQUE du funnel Cours d'été — monté une seule fois au-dessus du
 * navigateur racine (RootNavigator), jamais par écran. L'état vit en mémoire et
 * est partagé nativement entre tous les écrans summer (web ET natif) : le
 * sessionStorage n'est plus qu'un filet de survie au refresh (web) — relu au
 * boot, jamais comme canal cross-écran. C'est ce qui supprime la classe de
 * races/clobbers de l'ancien montage par-écran (N providers collés par
 * sessionStorage, où un provider périmé pouvait écraser l'état d'un autre).
 */
export const SummerFunnelProvider = ({ children }) => {
    const [state, setState] = useState(() =>
        withOfferedSlotIds({
            ...SUMMER_FUNNEL_INITIAL,
            ...(loadFromStorage() || {}),
        })
    )

    const update = useCallback((patch) => {
        setState((prev) => {
            const next = withOfferedSlotIds({ ...prev, ...patch })
            saveToStorage(next)
            return next
        })
    }, [])

    const reset = useCallback(() => {
        clearStorage()
        setState(SUMMER_FUNNEL_INITIAL)
    }, [])

    const value = useMemo(
        () => ({ state, update, reset }),
        [state, update, reset]
    )

    return (
        <SummerFunnelContext.Provider value={value}>
            {children}
        </SummerFunnelContext.Provider>
    )
}

export const useSummerFunnel = () => {
    const ctx = useContext(SummerFunnelContext)
    if (ctx) return ctx
    return {
        state: SUMMER_FUNNEL_INITIAL,
        update: () => {},
        reset: () => {},
    }
}
